<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import TopNavigationMenu from "./TopNavigationMenu.vue";
import useManageEventHelper from "@/modules/useManageEventHelper";
import { vClickOutside } from '@/directives/clickOutside';
const { isNew } = useManageEventHelper();

const route = useRoute();
const router = useRouter();
const showNav = ref(false);
const isQRCodeModalOpen = ref(false);

// Add a debounce flag to prevent rapid toggling
let isProcessingClick = false;

// Methods to update QR code modal state
const setQRCodeModalOpen = () => {
  isQRCodeModalOpen.value = true;
  console.log('QR code modal opened');
};

const openNav = () => {
  if (isProcessingClick) return;
  isProcessingClick = true;

  showNav.value = true;
  console.log('Menu opened');

  // Reset the flag after a delay
  setTimeout(() => {
    isProcessingClick = false;
  }, 300);
};

const closeNav = () => {
  if (isProcessingClick) return;
  isProcessingClick = true;

  // If QR code modal is open, add a delay to allow it to close first
  if (isQRCodeModalOpen.value) {
    console.log('QR code modal is open, delaying menu close');
    setTimeout(() => {
      isQRCodeModalOpen.value = false;
      showNav.value = false;
      console.log('Menu closed after delay');

      // Reset the flag after a delay
      setTimeout(() => {
        isProcessingClick = false;
      }, 300);
    }, 5); // 5ms delay to allow QR code modal to close
  } else {
    showNav.value = false;
    console.log('Menu closed immediately');

    // Reset the flag after a delay
    setTimeout(() => {
      isProcessingClick = false;
    }, 300);
  }
};

function adjustFixedTopElement() {
  const element = document.querySelector('#top-nav') as HTMLElement;
  if (!element) return;

  const rect = element.getBoundingClientRect();
  if (rect.y < 0) {
    element.style.position = 'relative';
    element.style.top = `${Math.abs(rect.y)}px`;
    element.style.marginBottom = `${Math.abs(rect.y)}px`;
  } else {
    element.style.position = 'static';
    element.style.top = 'auto';
    element.style.marginBottom = `0px`;
  }
}

watch(route, () => {
  (showNav.value = false);
  adjustFixedTopElement();
});

const share = () => {
  // Implement share functionality if needed
};

// Check if we're in the new event setup flow
const isInNewEventSetup = computed(() => {
  return isNew.value && route.meta?.isSetupFlow === true;
});

const backToHome = () => {
  const currentRouteName = route.name as string;

  // Special handling for new event setup flow
  if (isInNewEventSetup.value) {
    // For ManageEventInfo, go to MyHostedEvents
    if (currentRouteName === 'ManageEventInfo') {
      router.replace({ name: 'MyHostedEvents' });
      return;
    }

    // For other pages in the setup flow, go to the previous page in the flow
    if (currentRouteName === 'ManageEventCard') {
      router.replace({ name: 'ManageEventInfo' });
      return;
    }

    if (currentRouteName === 'ManageEventStopEdit') {
      router.replace({ name: 'ManageEventCard' });
      return;
    }

    if (currentRouteName === 'ManageEventFees') {
      router.replace({ name: 'ManageEventStopEdit', params: { order: 1 } });
      return;
    }
  }

  // First check for specific backPage
  if (route.meta?.backPage) {
    router.replace({ name: route.meta.backPage as string });
    return;
  }

  // Then check for cancelRoute
  if (route.meta?.cancelRoute) {
    router.replace({ name: route.meta.cancelRoute as string });
    return;
  }

  // Default to browser back if no metadata is available
  router.back();
};


</script>

<template>
  <div>
    <!-- Navigation Bar -->
    <nav id="top-nav" class="fixed top-0 left-0 right-0 bg-primary bg-contrastbrand shadow-md z-50 notification-aware">
      <div class="flex items-center justify-between p-4">
        <div>
          <template v-if="route.meta?.backPage || (route.meta?.actions as any)?.back">
            <a class="flex items-center mr-2 text-white" @click="backToHome">
              <i class="fas fa-angle-left text-xl"></i>
            </a>
          </template>
        </div>

        <a v-if="(route.meta?.actions as any)?.share" class="hidden md:block" @click="share"><i class="fas fa-share-nodes mr-1" />
        </a>
        <!-- Hamburger Menu (shown when menu is closed) -->
        <div
          v-if="!showNav"
          class="flex flex-col items-center md:hidden cursor-pointer"
          @click.stop="openNav"
        >
          <span class="block w-6 h-[2px] bg-white mb-1"></span>
          <span class="block w-6 h-[2px] bg-white mb-1"></span>
          <span class="block w-6 h-[2px] bg-white"></span>
        </div>

        <!-- X Menu (shown when menu is open) -->
        <div
          v-else
          class="flex flex-col items-center md:hidden cursor-pointer"
          @click.stop="closeNav"
        >
          <span class="block w-6 h-[2px] bg-white mb-1 rotate-45 translate-y-[5px]"></span>
          <span class="block w-6 h-[2px] bg-white mb-1 opacity-0"></span>
          <span class="block w-6 h-[2px] bg-white -rotate-45 -translate-y-[5px]"></span>
        </div>

      </div>
    </nav>

    <!-- Menu Overlay - removed to keep main content visible -->

    <!-- Menu Content -->
    <transition name="menu-fade">
      <div
        v-if="showNav"
        class="fixed left-0 right-0 top-[60px] z-50 bg-blue-50 shadow-lg w-full"
        v-click-outside="closeNav"
        @click.stop
      >
        <div class="flex flex-col md:flex-row md:items-center md:justify-between w-full">
          <TopNavigationMenu
            @close-nav="closeNav"
            @qr-modal-opened="setQRCodeModalOpen"
          />
        </div>
      </div>
    </transition>
  </div>

</template>
<style scoped>
#menu {
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
}

#menu .router-link-active,
#menu .navbar-item:hover {
  font-weight: 700;
  text-decoration: underline;
  background-color: transparent;
}

/* Menu transition */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
